import { useEffect, useMemo, useRef, useState } from "react";
import "./InternalChatV1.css";
import { supabase } from "../services/supabaseClient.js";

const CHAT_STORAGE_KEY = "framesbay_internal_chat_v1";
const PEOPLE_RESULT_LIMIT = 500;
const GROUP_MEMBER_LIMIT = 500;

function nowIso() {
  return new Date().toISOString();
}

function makeId(prefix = "chat") {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function initials(name = "User") {
  return String(name || "User")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "U";
}

function employeeName(employee) {
  return employee?.fullName || employee?.name || employee?.email || employee?.phone || "Employee";
}

function employeePhone(employee) {
  return String(employee?.phone || employee?.mobile || employee?.phoneNumber || "").replace(/\s+/g, "");
}

function searchableEmployeeText(employee, storeName = "") {
  return [employeeName(employee), employee?.phone, employee?.email, employee?.employeeCode, employee?.designation, storeName]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function isGroupAdmin(user) {
  return Boolean(
    user?.role === "super_admin" ||
    user?.role === "admin" ||
    user?.permissions?.all ||
    user?.permissions?.can_manage_users ||
    user?.permissions?.can_manage_roles ||
    user?.permissions?.communication
  );
}

function canDownloadAttachments(user) {
  return Boolean(
    user?.role === "super_admin" ||
    user?.permissions?.all ||
    user?.permissions?.can_download_chat_attachments ||
    user?.permissions?.can_manage_users
  );
}

function fileSizeLabel(size = 0) {
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function readFileAsAttachment(file) {
  return new Promise((resolve) => {
    const attachment = {
      id: makeId("att"),
      name: file.name || "Attachment",
      type: file.type || "file",
      size: file.size || 0,
      dataUrl: ""
    };
    if (!file.type?.startsWith("image/")) {
      resolve(attachment);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve({ ...attachment, dataUrl: String(reader.result || "") });
    reader.onerror = () => resolve(attachment);
    reader.readAsDataURL(file);
  });
}

function normalizeEmployee(employee) {
  const id = employee?.id || employee?.employeeId || employee?.phone || employee?.email || makeId("employee");
  return { ...employee, id };
}

function resolveCurrentEmployee(rawEmployees, currentEmployee, user) {
  if (currentEmployee?.id) return normalizeEmployee(currentEmployee);
  const userEmployeeId = user?.employeeId || "";
  const userEmail = String(user?.email || "").toLowerCase();
  const userPhone = String(user?.phone || "").replace(/\D/g, "");
  const found = (rawEmployees || []).find((employee) => {
    const normalized = normalizeEmployee(employee);
    const empEmail = String(employee?.email || "").toLowerCase();
    const empPhone = String(employee?.phone || "").replace(/\D/g, "");
    return normalized.id === userEmployeeId || employee?.employeeId === userEmployeeId || (userEmail && empEmail === userEmail) || (userPhone && empPhone === userPhone);
  });
  if (found) return normalizeEmployee(found);
  return null;
}

function safeEmployeeList(employees = [], resolvedCurrentEmployee, user) {
  const currentId = resolvedCurrentEmployee?.id || user?.employeeId || user?.id || "me";
  const byId = new Map();
  employees
    .filter((employee) => employee && employee.active !== false)
    .map(normalizeEmployee)
    .forEach((employee) => byId.set(employee.id, employee));

  if (!byId.has(currentId)) {
    byId.set(currentId, {
      id: currentId,
      fullName: resolvedCurrentEmployee?.fullName || user?.name || user?.email || "Me",
      designation: resolvedCurrentEmployee?.designation || "Current user",
      storeId: resolvedCurrentEmployee?.storeId || ""
    });
  }
  return Array.from(byId.values());
}

function createInitialState(staff, currentEmployeeId) {
  const allMemberIds = [...new Set(staff.map((employee) => employee.id).filter(Boolean))];
  return {
    threads: [
      {
        id: "group-general",
        type: "group",
        name: "All Staff",
        description: "Company-wide staff group",
        memberIds: allMemberIds.includes(currentEmployeeId) ? allMemberIds : [currentEmployeeId, ...allMemberIds],
        systemGroup: true,
        createdAt: nowIso(),
        updatedAt: nowIso(),
        messages: [
          {
            id: makeId("msg"),
            senderId: currentEmployeeId,
            text: "Internal chat started.",
            sentAt: nowIso()
          }
        ]
      }
    ]
  };
}

function syncAllStaffThread(current, staff, currentEmployeeId) {
  const allMemberIds = [...new Set([currentEmployeeId, ...staff.map((employee) => employee.id).filter(Boolean)])];
  const threads = Array.isArray(current?.threads) ? current.threads : [];
  const existingIndex = threads.findIndex((thread) => thread.id === "group-general" || thread.name === "All Staff");
  if (existingIndex === -1) {
    return { ...current, threads: [createInitialState(staff, currentEmployeeId).threads[0], ...threads] };
  }
  return {
    ...current,
    threads: threads.map((thread, index) => index === existingIndex ? {
      ...thread,
      id: "group-general",
      type: "group",
      name: "All Staff",
      description: "Company-wide staff group",
      memberIds: allMemberIds,
      systemGroup: true,
      updatedAt: thread.updatedAt || nowIso(),
      messages: Array.isArray(thread.messages) ? thread.messages : []
    } : thread)
  };
}

function loadStoredState(staff, currentEmployeeId) {
  try {
    const raw = window.localStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return createInitialState(staff, currentEmployeeId);
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.threads)) return createInitialState(staff, currentEmployeeId);
    return syncAllStaffThread(parsed, staff, currentEmployeeId);
  } catch {
    return createInitialState(staff, currentEmployeeId);
  }
}

function threadTitle(thread, staffById, currentEmployeeId) {
  if (thread.type === "direct") {
    const otherId = thread.memberIds.find((id) => id !== currentEmployeeId) || thread.memberIds[0];
    return employeeName(staffById.get(otherId)) || "Direct Chat";
  }
  return thread.name || "Group";
}

function lastMessage(thread) {
  return thread.messages?.[thread.messages.length - 1] || null;
}

function messagePreview(message) {
  if (!message) return "No messages yet";
  if (message.text) return message.text;
  if (message.attachments?.length) return `${message.attachments.length} attachment${message.attachments.length > 1 ? "s" : ""}`;
  return "No messages yet";
}

function replyPreviewText(message) {
  if (!message) return "Message";
  if (message.text) return message.text;
  if (message.attachments?.length) return `${message.attachments.length} attachment${message.attachments.length > 1 ? "s" : ""}`;
  return "Message";
}

function normalizeUserAsEmployee(login, employees = []) {
  if (!login || login.active === false) return null;
  const linkedEmployee = employees.find((employee) => employee?.id === login.employeeId) || {};
  return normalizeEmployee({
    ...linkedEmployee,
    id: login.employeeId || linkedEmployee.id || login.id || login.email,
    employeeId: login.employeeId || linkedEmployee.id || login.id,
    fullName: linkedEmployee.fullName || login.fullName || login.name || login.email || "Employee",
    phone: linkedEmployee.phone || login.phone || login.mobile || login.phoneNumber || "",
    email: linkedEmployee.email || login.email || "",
    designation: linkedEmployee.designation || login.designation || login.role || "Employee",
    storeId: linkedEmployee.storeId || login.storeId || login.storeIds?.[0] || "",
    active: linkedEmployee.active !== false && login.active !== false
  });
}

function mergeEmployeeLists(...lists) {
  const byId = new Map();
  lists.flat().filter(Boolean).forEach((item) => {
    const employee = normalizeEmployee(item);
    const existing = byId.get(employee.id) || {};
    byId.set(employee.id, {
      ...existing,
      ...employee,
      fullName: employee.fullName || employee.name || existing.fullName || existing.name || employee.email || employee.phone || "Employee",
      phone: employee.phone || employee.mobile || employee.phoneNumber || existing.phone || existing.mobile || existing.phoneNumber || "",
      email: employee.email || existing.email || "",
      designation: employee.designation || existing.designation || employee.role || existing.role || "Employee",
      storeId: employee.storeId || existing.storeId || employee.storeIds?.[0] || existing.storeIds?.[0] || "",
      active: employee.active !== false && existing.active !== false
    });
  });
  return Array.from(byId.values()).filter((employee) => employee.active !== false);
}

export default function InternalChatV1({ employees = [], stores = [], users = [], user, currentEmployee }) {
  const [remoteEmployees, setRemoteEmployees] = useState([]);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const localEmployees = Array.isArray(employees) ? employees : [];
  const localUsers = Array.isArray(users) ? users : [];
  const rawEmployees = useMemo(() => mergeEmployeeLists(
    localEmployees,
    remoteEmployees,
    localUsers.map((login) => normalizeUserAsEmployee(login, localEmployees)),
    remoteUsers.map((login) => normalizeUserAsEmployee(login, [...localEmployees, ...remoteEmployees]))
  ), [localEmployees, localUsers, remoteEmployees, remoteUsers]);
  const resolvedCurrentEmployee = useMemo(() => resolveCurrentEmployee(rawEmployees, currentEmployee, user), [rawEmployees, currentEmployee, user]);
  const currentEmployeeId = resolvedCurrentEmployee?.id || user?.employeeId || user?.id || "me";
  const staff = useMemo(() => safeEmployeeList(rawEmployees, resolvedCurrentEmployee, user), [rawEmployees, resolvedCurrentEmployee, user]);
  const staffById = useMemo(() => new Map(staff.map((employee) => [employee.id, employee])), [staff]);
  const storesById = useMemo(() => new Map((stores || []).map((store) => [store.id, store])), [stores]);
  const canAdminManageGroups = isGroupAdmin(user);
  const canDownload = canDownloadAttachments(user);

  const [chatState, setChatState] = useState(() => loadStoredState(staff, currentEmployeeId));
  const [selectedThreadId, setSelectedThreadId] = useState("");
  const [search, setSearch] = useState("");
  const [messageText, setMessageText] = useState("");
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupMemberIds, setGroupMemberIds] = useState([currentEmployeeId]);
  const [groupMemberSearch, setGroupMemberSearch] = useState("");
  const [detailsMemberSearch, setDetailsMemberSearch] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [callModal, setCallModal] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function loadChatDirectory() {
      try {
        if (!supabase) return;
        const [employeesResult, usersResult] = await Promise.all([
          supabase.from("employees").select("*"),
          supabase.from("users").select("*")
        ]);
        if (cancelled) return;
        if (!employeesResult.error && Array.isArray(employeesResult.data)) setRemoteEmployees(employeesResult.data);
        if (!usersResult.error && Array.isArray(usersResult.data)) setRemoteUsers(usersResult.data);
      } catch (error) {
        console.warn("Chat directory load failed", error);
      }
    }
    loadChatDirectory();
    return () => { cancelled = true; };
  }, []);

  const storeName = (storeId) => {
    const store = storesById.get(storeId);
    return store?.name || store?.code || "";
  };

  useEffect(() => {
    window.localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(chatState));
  }, [chatState]);

  useEffect(() => {
    setChatState((current) => syncAllStaffThread(current, staff, currentEmployeeId));
  }, [staff, currentEmployeeId]);

  const query = search.trim().toLowerCase();
  const memberVisibleThreads = useMemo(() => {
    return (chatState.threads || []).filter((thread) => thread.memberIds?.includes(currentEmployeeId));
  }, [chatState.threads, currentEmployeeId]);

  const visibleThreads = useMemo(() => {
    return [...memberVisibleThreads]
      .filter((thread) => {
        if (!query) return true;
        const title = threadTitle(thread, staffById, currentEmployeeId).toLowerCase();
        const memberText = (thread.memberIds || []).map((id) => employeeName(staffById.get(id))).join(" ").toLowerCase();
        const text = (thread.messages || []).map((message) => message.text).join(" ").toLowerCase();
        return title.includes(query) || memberText.includes(query) || text.includes(query);
      })
      .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());
  }, [currentEmployeeId, memberVisibleThreads, query, staffById]);

  const peopleResults = useMemo(() => {
    return staff
      .filter((employee) => employee.id !== currentEmployeeId)
      .filter((employee) => !query || searchableEmployeeText(employee, storeName(employee.storeId)).includes(query))
      .sort((a, b) => employeeName(a).localeCompare(employeeName(b)))
      .slice(0, PEOPLE_RESULT_LIMIT);
  }, [currentEmployeeId, query, staff, storesById]);

  const selectedThread = useMemo(() => {
    const found = memberVisibleThreads.find((thread) => thread.id === selectedThreadId);
    return found || null;
  }, [memberVisibleThreads, selectedThreadId]);

  const selectedMembers = selectedThread?.memberIds?.map((id) => staffById.get(id)).filter(Boolean) || [];
  const directOtherEmployee = selectedThread?.type === "direct"
    ? staffById.get(selectedThread.memberIds.find((id) => id !== currentEmployeeId))
    : null;
  const canManageSelectedGroup = Boolean(
    selectedThread?.type === "group" &&
    !selectedThread?.systemGroup &&
    (canAdminManageGroups || selectedThread?.createdById === currentEmployeeId)
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [selectedThread?.messages?.length, selectedThreadId]);

  useEffect(() => {
    setReplyToMessage(null);
  }, [selectedThreadId]);

  const openThread = (threadId) => {
    setSelectedThreadId(threadId);
    setMobileChatOpen(true);
    setDetailsOpen(false);
    setTimeout(() => messageInputRef.current?.focus?.(), 100);
  };

  const startDirectChat = (employeeId) => {
    if (!employeeId || employeeId === currentEmployeeId) return;
    const existing = (chatState.threads || []).find((thread) => (
      thread.type === "direct" && thread.memberIds.includes(currentEmployeeId) && thread.memberIds.includes(employeeId)
    ));
    if (existing) {
      openThread(existing.id);
      setSearch("");
      return;
    }
    const thread = {
      id: makeId("direct"),
      type: "direct",
      name: "",
      memberIds: [currentEmployeeId, employeeId],
      createdAt: nowIso(),
      updatedAt: nowIso(),
      messages: []
    };
    setChatState((current) => ({ ...current, threads: [thread, ...(current.threads || [])] }));
    setSelectedThreadId(thread.id);
    setMobileChatOpen(true);
    setSearch("");
    setDetailsOpen(false);
    setTimeout(() => messageInputRef.current?.focus?.(), 120);
  };

  const sendMessage = () => {
    const text = messageText.trim();
    if ((!text && !pendingAttachments.length) || !selectedThread) return;
    const msg = {
      id: makeId("msg"),
      senderId: currentEmployeeId,
      text,
      attachments: pendingAttachments,
      replyTo: replyToMessage ? {
        id: replyToMessage.id,
        senderName: employeeName(staffById.get(replyToMessage.senderId)),
        text: replyPreviewText(replyToMessage)
      } : null,
      sentAt: nowIso()
    };
    setChatState((current) => ({
      ...current,
      threads: (current.threads || []).map((thread) => (
        thread.id === selectedThread.id ? { ...thread, messages: [...(thread.messages || []), msg], updatedAt: nowIso() } : thread
      ))
    }));
    setMessageText("");
    setReplyToMessage(null);
    setPendingAttachments([]);
  };

  const addAttachments = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const attachments = await Promise.all(files.map(readFileAsAttachment));
    setPendingAttachments((current) => [...current, ...attachments].slice(0, 10));
    event.target.value = "";
  };

  const removePendingAttachment = (attachmentId) => {
    setPendingAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
  };

  const createGroup = () => {
    const name = groupName.trim();
    const uniqueMemberIds = [...new Set([currentEmployeeId, ...groupMemberIds])];
    if (!name || uniqueMemberIds.length < 2) return;
    const thread = {
      id: makeId("group"),
      type: "group",
      name,
      description: `${uniqueMemberIds.length} members`,
      memberIds: uniqueMemberIds,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      createdById: currentEmployeeId,
      messages: [{ id: makeId("msg"), senderId: currentEmployeeId, text: `${employeeName(staffById.get(currentEmployeeId))} created ${name}.`, sentAt: nowIso() }]
    };
    setChatState((current) => ({ ...current, threads: [thread, ...(current.threads || [])] }));
    setSelectedThreadId(thread.id);
    setMobileChatOpen(true);
    setGroupName("");
    setGroupMemberIds([currentEmployeeId]);
    setGroupMemberSearch("");
    setGroupModalOpen(false);
  };

  const toggleGroupMember = (employeeId) => {
    setGroupMemberIds((current) => (current.includes(employeeId) ? current.filter((id) => id !== employeeId) : [...current, employeeId]));
  };

  const toggleThreadMember = (employeeId) => {
    if (!canManageSelectedGroup || !selectedThread || selectedThread.type !== "group") return;
    setChatState((current) => ({
      ...current,
      threads: (current.threads || []).map((thread) => {
        if (thread.id !== selectedThread.id) return thread;
        const nextMembers = thread.memberIds.includes(employeeId)
          ? thread.memberIds.filter((id) => id !== employeeId)
          : [...thread.memberIds, employeeId];
        return { ...thread, memberIds: nextMembers.includes(currentEmployeeId) ? nextMembers : [currentEmployeeId, ...nextMembers], updatedAt: nowIso() };
      })
    }));
  };

  const groupMemberResults = useMemo(() => {
    const q = groupMemberSearch.trim().toLowerCase();
    return staff.filter((employee) => employee.id !== currentEmployeeId).filter((employee) => !q || searchableEmployeeText(employee, storeName(employee.storeId)).includes(q)).slice(0, GROUP_MEMBER_LIMIT);
  }, [currentEmployeeId, groupMemberSearch, staff, storesById]);

  const detailsMemberResults = useMemo(() => {
    const q = detailsMemberSearch.trim().toLowerCase();
    return staff.filter((employee) => employee.id !== currentEmployeeId).filter((employee) => !q || searchableEmployeeText(employee, storeName(employee.storeId)).includes(q)).slice(0, GROUP_MEMBER_LIMIT);
  }, [currentEmployeeId, detailsMemberSearch, staff, storesById]);

  const selectAttachment = (attachment) => {
    if (attachment?.dataUrl) {
      setPreviewZoom(1);
      setPreviewAttachment(attachment);
    }
  };

  const handleReply = (message) => {
    setReplyToMessage(message);
    setMobileChatOpen(true);
    setTimeout(() => messageInputRef.current?.focus?.(), 100);
  };

  const startVoiceCall = () => {
    if (!directOtherEmployee) return;
    setCallModal({
      type: "In-app voice call",
      name: employeeName(directOtherEmployee),
      note: "Phone dial removed. This is the web-call screen; live audio needs WebRTC signalling + realtime backend in the next step."
    });
  };

  const startVideoCall = () => {
    if (!directOtherEmployee) return;
    setCallModal({
      type: "In-app video call",
      name: employeeName(directOtherEmployee),
      note: "Phone dial removed. This is the web-call screen; live video needs WebRTC signalling + realtime backend in the next step."
    });
  };

  return (
    <section className={`chatv1-shell ${mobileChatOpen ? "chat-open" : ""}`}>
      <div className="chatv1-appbar">
        <div>
          <span>Internal Chat</span>
          <h1>Messages</h1>
        </div>
        <div className="chatv1-appbar-actions">
          <button type="button" onClick={() => { setMobileChatOpen(false); setSelectedThreadId(""); }}>＋ New Chat</button>
          <button type="button" onClick={() => setGroupModalOpen(true)}>👥 Group</button>
        </div>
      </div>

      <div className="chatv1-layout">
        <aside className="chatv1-sidebar">
          <div className="chatv1-search">
            <span>⌕</span>
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search employee, group or chat" />
          </div>

          <div className="chatv1-thread-list">
            <p className="chatv1-list-title">People — tap Message to start direct chat</p>
            {peopleResults.length ? peopleResults.map((employee) => (
              <div key={employee.id} className="chatv1-contact-row">
                <button type="button" className="chatv1-contact-main" onClick={() => startDirectChat(employee.id)}>
                  <span className="chatv1-avatar">{initials(employeeName(employee))}</span>
                  <span>
                    <strong>{employeeName(employee)}</strong>
                    <small>{employee.designation || storeName(employee.storeId) || employee.phone || employee.email || "Employee"}</small>
                  </span>
                </button>
                <button type="button" className="chatv1-mini-action" onClick={() => startDirectChat(employee.id)}>Message</button>
              </div>
            )) : <div className="chatv1-empty compact"><span>No people found</span></div>}

            <p className="chatv1-list-title">Chats / Groups</p>
            {visibleThreads.length ? visibleThreads.map((thread) => {
              const title = threadTitle(thread, staffById, currentEmployeeId);
              const last = lastMessage(thread);
              return (
                <button key={thread.id} type="button" className={`chatv1-thread ${selectedThread?.id === thread.id ? "active" : ""}`} onClick={() => openThread(thread.id)}>
                  <span className="chatv1-avatar">{thread.type === "group" ? "👥" : initials(title)}</span>
                  <span className="chatv1-thread-copy">
                    <strong>{title}</strong>
                    <small>{messagePreview(last)}</small>
                  </span>
                  <em>{last ? formatTime(last.sentAt).split(",").pop() : ""}</em>
                </button>
              );
            }) : <div className="chatv1-empty compact"><b>No chats</b><span>Tap any employee above.</span></div>}
          </div>
        </aside>

        <main className="chatv1-chat-panel">
          {selectedThread ? (
            <>
              <div className="chatv1-chat-head">
                <button className="chatv1-back" type="button" onClick={() => setMobileChatOpen(false)}>‹</button>
                <span className="chatv1-avatar big">{selectedThread.type === "group" ? "👥" : initials(threadTitle(selectedThread, staffById, currentEmployeeId))}</span>
                <div>
                  <h2>{threadTitle(selectedThread, staffById, currentEmployeeId)}</h2>
                  <small>{selectedThread.type === "group" ? `${selectedThread.memberIds.length} members` : "Direct message"}</small>
                </div>
                {selectedThread.type === "direct" ? (
                  <>
                    <button className="chatv1-call-btn" type="button" onClick={startVoiceCall}>☎</button>
                    <button className="chatv1-call-btn" type="button" onClick={startVideoCall}>🎥</button>
                  </>
                ) : null}
                <button className="chatv1-detail-toggle" type="button" onClick={() => setDetailsOpen(true)}>Info</button>
              </div>

              <div className="chatv1-messages">
                {(selectedThread.messages || []).length ? selectedThread.messages.map((message) => {
                  const mine = message.senderId === currentEmployeeId;
                  const sender = staffById.get(message.senderId);
                  return (
                    <article key={message.id} className={`chatv1-message ${mine ? "mine" : ""}`}>
                      {!mine ? <strong>{employeeName(sender)}</strong> : null}
                      {message.replyTo ? (
                        <div className="chatv1-reply-quote">
                          <small>{message.replyTo.senderName || "Message"}</small>
                          <span>{message.replyTo.text}</span>
                        </div>
                      ) : null}
                      {message.text ? <p>{message.text}</p> : null}
                      {message.attachments?.length ? (
                        <div className="chatv1-message-files">
                          {message.attachments.map((attachment) => (
                            <button key={attachment.id} type="button" className={`chatv1-message-file ${attachment.dataUrl ? "image" : "doc"}`} onClick={() => selectAttachment(attachment)}>
                              {attachment.dataUrl ? <img src={attachment.dataUrl} alt={attachment.name} /> : <b>📎</b>}
                              <span>{attachment.name}</span>
                              <small>{attachment.dataUrl ? "Tap to view" : fileSizeLabel(attachment.size)}</small>
                            </button>
                          ))}
                        </div>
                      ) : null}
                      <div className="chatv1-message-actions">
                        <button type="button" onClick={() => handleReply(message)}>Reply</button>
                      </div>
                      <span>{formatTime(message.sentAt)}</span>
                    </article>
                  );
                }) : <div className="chatv1-empty centered"><b>No messages yet</b><span>Type below and send the first message.</span></div>}
                <div ref={messagesEndRef} />
              </div>

              {replyToMessage ? (
                <div className="chatv1-replying-bar">
                  <div>
                    <strong>Replying to {employeeName(staffById.get(replyToMessage.senderId))}</strong>
                    <span>{replyPreviewText(replyToMessage)}</span>
                  </div>
                  <button type="button" onClick={() => setReplyToMessage(null)}>×</button>
                </div>
              ) : null}

              {pendingAttachments.length ? (
                <div className="chatv1-attachment-tray">
                  {pendingAttachments.map((attachment) => (
                    <div key={attachment.id} className="chatv1-attachment-chip">
                      {attachment.dataUrl ? <img src={attachment.dataUrl} alt={attachment.name} /> : <span>📎</span>}
                      <small>{attachment.name}</small>
                      <button type="button" onClick={() => removePendingAttachment(attachment.id)}>×</button>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="chatv1-composer">
                <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={addAttachments} hidden />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={addAttachments} hidden />
                <button className="chatv1-icon-btn" type="button" onClick={() => fileInputRef.current?.click()}>＋</button>
                <button className="chatv1-icon-btn" type="button" onClick={() => cameraInputRef.current?.click()}>📷</button>
                <textarea ref={messageInputRef} value={messageText} onChange={(event) => setMessageText(event.target.value)} placeholder="Message" onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendMessage(); } }} />
                <button className="chatv1-send" type="button" onClick={sendMessage}>➤</button>
              </div>
            </>
          ) : <div className="chatv1-empty centered"><b>Select an employee</b><span>Tap Message on any employee to start a direct chat.</span></div>}
        </main>

        <aside className={`chatv1-details ${detailsOpen ? "open" : ""}`}>
          <button className="chatv1-details-close" type="button" onClick={() => setDetailsOpen(false)}>×</button>
          <h2>Chat info</h2>
          {selectedThread ? (
            <>
              <div className="chatv1-detail-card"><span>Visibility</span><strong>{selectedThread.type === "direct" ? "Only these 2 employees" : "Members only"}</strong></div>
              <div className="chatv1-detail-card"><span>Attachments</span><strong>{canDownload ? "Download allowed" : "View only"}</strong></div>
              <div className="chatv1-member-list">
                {selectedMembers.map((employee) => (
                  <div key={employee.id} className="chatv1-member">
                    <span className="chatv1-avatar">{initials(employeeName(employee))}</span>
                    <div><strong>{employeeName(employee)}</strong><small>{employee.designation || storeName(employee.storeId) || "Employee"}</small></div>
                  </div>
                ))}
              </div>
              {selectedThread.type === "group" ? (
                canManageSelectedGroup ? (
                  <>
                    <h3>Add / remove members</h3>
                    <div className="chatv1-member-search"><span>⌕</span><input value={detailsMemberSearch} onChange={(event) => setDetailsMemberSearch(event.target.value)} placeholder="Search employee" /></div>
                    <div className="chatv1-check-list">
                      {detailsMemberResults.map((employee) => (
                        <label key={employee.id}>
                          <input type="checkbox" checked={selectedThread.memberIds.includes(employee.id)} onChange={() => toggleThreadMember(employee.id)} />
                          <span>{employeeName(employee)}</span>
                          <small>{employee.designation || storeName(employee.storeId) || ""}</small>
                        </label>
                      ))}
                    </div>
                  </>
                ) : <div className="chatv1-warning">Only the group creator or admin can add/remove group members. All Staff auto-includes active employees.</div>
              ) : null}
            </>
          ) : null}
        </aside>
      </div>

      {groupModalOpen ? (
        <div className="chatv1-modal" role="dialog" aria-modal="true">
          <button className="chatv1-scrim" type="button" onClick={() => setGroupModalOpen(false)} aria-label="Close" />
          <section className="chatv1-sheet">
            <div className="chatv1-sheet-head"><h2>Create group</h2><button type="button" onClick={() => setGroupModalOpen(false)}>×</button></div>
            <label className="chatv1-field"><span>Group name</span><input value={groupName} onChange={(event) => setGroupName(event.target.value)} placeholder="Example: Noida Store Team" autoFocus /></label>
            <div className="chatv1-member-search modal-search"><span>⌕</span><input value={groupMemberSearch} onChange={(event) => setGroupMemberSearch(event.target.value)} placeholder="Search employee by name, phone, store or role" /></div>
            <p className="chatv1-mini-title">Selected: {groupMemberIds.length}</p>
            <div className="chatv1-check-list modal-list">
              {groupMemberResults.map((employee) => (
                <label key={employee.id}>
                  <input type="checkbox" checked={groupMemberIds.includes(employee.id)} onChange={() => toggleGroupMember(employee.id)} />
                  <span>{employeeName(employee)}</span>
                  <small>{employee.designation || storeName(employee.storeId) || ""}</small>
                </label>
              ))}
            </div>
            <button className="chatv1-primary full" type="button" onClick={createGroup}>Create Group</button>
          </section>
        </div>
      ) : null}

      {previewAttachment ? (
        <div className="chatv1-image-modal" role="dialog" aria-modal="true">
          <button className="chatv1-scrim" type="button" onClick={() => setPreviewAttachment(null)} aria-label="Close" />
          <section className="chatv1-image-viewer">
            <div className="chatv1-image-head">
              <strong>{previewAttachment.name}</strong>
              <div>
                <button type="button" onClick={() => setPreviewZoom((value) => Math.max(0.75, value - 0.25))}>−</button>
                <button type="button" onClick={() => setPreviewZoom((value) => Math.min(3, value + 0.25))}>＋</button>
                {canDownload ? <a href={previewAttachment.dataUrl} download={previewAttachment.name}>Download</a> : <span>View only</span>}
                <button type="button" onClick={() => setPreviewAttachment(null)}>×</button>
              </div>
            </div>
            <div className="chatv1-zoom-stage"><img src={previewAttachment.dataUrl} alt={previewAttachment.name} style={{ transform: `scale(${previewZoom})` }} /></div>
          </section>
        </div>
      ) : null}

      {callModal ? (
        <div className="chatv1-modal" role="dialog" aria-modal="true">
          <button className="chatv1-scrim" type="button" onClick={() => setCallModal(null)} aria-label="Close" />
          <section className="chatv1-call-sheet">
            <div className="chatv1-avatar call-avatar">{initials(callModal.name)}</div>
            <h2>{callModal.type}</h2>
            <p>{callModal.name}</p>
            <small>{callModal.note}</small>
            <button type="button" onClick={() => setCallModal(null)}>End</button>
          </section>
        </div>
      ) : null}
    </section>
  );
}
